// Offline-tolerant check-in queue.
//
// The scanner uses this so a check-in is never lost when reception wifi drops.
// We store queued check-ins in localStorage, expose them to the UI as
// "will sync" rows, and flush them the moment the network is back.

const KEY = 'bb_offline_checkins_v1';

function readAll() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeAll(list) {
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch {}
}

export function enqueueCheckIn(entry) {
  const list = readAll();
  list.push({ ...entry, queued_at: new Date().toISOString(), attempts: 0 });
  writeAll(list);
  window.dispatchEvent(new Event('bb-queue-updated'));
}

export function getQueue() { return readAll(); }

export function removeFromQueue(bookingNumber) {
  const list = readAll().filter((e) => e.booking_number !== bookingNumber);
  writeAll(list);
  window.dispatchEvent(new Event('bb-queue-updated'));
}

export function isQueued(bookingNumber) {
  return readAll().some((e) => e.booking_number === bookingNumber);
}

/**
 * Attempt to flush every queued check-in. postFn(entry) -> Promise; resolve on
 * success, reject with any error. 4xx errors from the server are considered
 * "resolved" (booking already checked in / can't be checked in) and removed
 * from the queue to avoid an infinite loop.
 */
export async function flushQueue(postFn, onProgress) {
  const list = readAll();
  if (list.length === 0) return { synced: 0, failed: 0 };
  let synced = 0, failed = 0;
  const remaining = [];
  for (const entry of list) {
    try {
      await postFn(entry);
      synced += 1;
      onProgress && onProgress({ entry, ok: true });
    } catch (e) {
      const status = e?.response?.status;
      // 4xx from server: guest already checked in, or bad state.
      // Drop it from the queue — retries won't help — but count as a failure so the UI can note it.
      if (status && status >= 400 && status < 500) {
        failed += 1;
        onProgress && onProgress({ entry, ok: false, dropped: true, message: e?.response?.data?.detail || 'Server rejected' });
      } else {
        // network / 5xx — keep for later
        remaining.push({ ...entry, attempts: (entry.attempts || 0) + 1 });
        failed += 1;
        onProgress && onProgress({ entry, ok: false, dropped: false, message: 'still offline' });
      }
    }
  }
  writeAll(remaining);
  window.dispatchEvent(new Event('bb-queue-updated'));
  return { synced, failed };
}

// Consumers subscribe to queue changes (updates + online/offline)
export function subscribeQueue(handler) {
  const onUpdate = () => handler();
  const onOnline = () => handler();
  window.addEventListener('bb-queue-updated', onUpdate);
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOnline);
  return () => {
    window.removeEventListener('bb-queue-updated', onUpdate);
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOnline);
  };
}
